import { EyeIcon, RefreshIcon, StyledIconButton } from "@aureskonnect/react-ui";
import { t } from "i18next";
import React from "react";
import { useSnapshot } from "valtio";
import {
    templateActions,
    templateStore,
} from "@pages/Applications/C&C/C&CSetup/templateStore";
import { LanguageKey, languages } from "@constants/template";
import { Table } from "reactstrap";
import { AvForm } from "availity-reactstrap-validation";
import { SwiperLanguagesWrapper } from "./Description/SwiperLanguagesWrapper";
import { store } from "../Website/CoverPage/store";

interface FieldConfig {
    key: string;
    label: string;
    placeholder?: string;
}

interface LanguageTableProps {
    path: string[];
    fields: FieldConfig[];
    defaultTexts: any;
    sectionTitle?: string;
    showPreview?: boolean;
}

export const AutoResizeTextarea = ({
    value,
    onChange,
    placeholder,
}: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
}) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [localValue, setLocalValue] = React.useState(value);
    const [isFocused, setIsFocused] = React.useState(false);

    // Sync external value only if not editing
    React.useEffect(() => {
        if (!isFocused) {
            setLocalValue(value);
        }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value: newValue, selectionStart, selectionEnd } = e.target;
        setLocalValue(newValue);

        // Preserve cursor position
        requestAnimationFrame(() => {
            if (textareaRef.current) {
                textareaRef.current.selectionStart = selectionStart;
                textareaRef.current.selectionEnd = selectionEnd;
            }
        });

        onChange(e); // Notify parent
    };

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    // Auto-resize textarea height based on content
    React.useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "0px";
            textarea.style.height = `${Math.max(textarea.scrollHeight, 20)}px`;
        }
    }, [localValue]);

    // Re-apply height when textarea appears in viewport
    React.useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        textarea.style.height = "0PX";
                        textarea.style.height = `${Math.max(
                            textarea.scrollHeight,
                            20
                        )}px`;
                    }
                });
            },
            { threshold: 0.1 }
        );

        observer.observe(textarea);
        return () => observer.disconnect();
    }, []);

    return (
        <textarea
            className="text-input"
            ref={textareaRef}
            value={localValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            style={{
                width: "100%",
                lineHeight: "1.5",
                padding: "8px",
                overflowY: "hidden",
                resize: "none",
            }}
        />
    );
};

const LanguageTable = React.memo(function LanguageTable({
    path = [],
    fields = [],
    defaultTexts = {},
    sectionTitle = "Content",
}: LanguageTableProps) {
    const snap = useSnapshot(templateStore);

    const getCurrentValue = React.useCallback(
        (field: string, lang: LanguageKey): string => {
            try {
                return (
                    path.reduce((acc: any, key) => acc?.[key], snap.template)?.[
                        field
                    ]?.[lang] || ""
                );
            } catch {
                return "";
            }
        },
        [snap.template, path]
    );

    const onTextChange = React.useCallback(
        (field: string, value: string, lang: LanguageKey) => {
            templateActions.updateLanguageContent(
                ["template", ...path, field],
                lang,
                value
            );
        },
        [path]
    );
    const handleResetFieldsLang = (activeLang: LanguageKey) => {
        fields.forEach((field) => {
            const defaultValue = defaultTexts[field.key]?.[activeLang] || "";
            onTextChange(field.key, defaultValue, activeLang);
        });
    };

    const handleResetAll = () => {
        fields.forEach((field) => {
            languages.forEach((lan) => {
                const defaultValue = defaultTexts[field.key]?.[lan.key] || "";
                onTextChange(field.key, defaultValue, lan.key);
            });
        });
    };
    const { selectedLanguage } = useSnapshot(store);

    return (
        <div
            className="mt-3 rounded border mx-2"
            style={{
                borderColor: "#CECECE",
            }}
        >
            <div
                className="d-flex justify-content-between align-items-center px-4 py-2 "
                style={{
                    backgroundColor: "#EDEDED",
                    height: "40px",
                    fontSize: "20px",
                }}
            >
                <div
                    className="text-left  d-flex align-items-center  cursor__clz"
                    style={{
                        top: "659px",
                        left: "360px",
                        textAlign: "left",
                        font: "normal normal 600 16px/21px Segoe UI",
                        letterSpacing: "0px",
                        color: "#000000",
                        opacity: 1,
                    }}
                >
                    {t(sectionTitle)}
                </div>
                <StyledIconButton
                    title={t("Reset")}
                    outline={true}
                    variant="danger"
                    className="m-0 pme_btn_buttonReinitializeTemplate"
                    onClick={handleResetAll}
                >
                    <RefreshIcon height={20} width={20} />
                </StyledIconButton>
            </div>

            <SwiperLanguagesWrapper />

            <div className="mt-3 mx-4">
                <AvForm>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th
                                    style={{
                                        width: "25%",
                                        verticalAlign: "middle",
                                    }}
                                ></th>
                                <th
                                    style={{
                                        width: "30%",
                                        verticalAlign: "middle",
                                    }}
                                >
                                    <div className="ml-2 d-flex align-items-center cursor__clz">
                                        {t("Default text")}
                                    </div>
                                </th>
                                <th
                                    style={{
                                        verticalAlign: "middle",
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="ml-2 cursor__clz">
                                            {t("Custom")}
                                        </div>

                                        <StyledIconButton
                                            title={t("Reset")}
                                            icon="RefreshIcon"
                                            style={{ margin: "0px" }}
                                            onClick={() => {
                                                handleResetFieldsLang(
                                                    selectedLanguage
                                                );
                                            }}
                                        >
                                            <RefreshIcon
                                                height={15}
                                                width={15}
                                            />
                                        </StyledIconButton>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((field) => (
                                <tr key={field.key}>
                                    <td
                                        style={{
                                            width: "25%",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <strong>{t(field.label)}</strong>
                                    </td>
                                    <td
                                        style={{
                                            width: "30%",
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <div className="default-text-content">
                                            {
                                                defaultTexts[field.key][
                                                    selectedLanguage
                                                ]
                                            }
                                        </div>
                                    </td>
                                    <td
                                        style={{
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: "10px",
                                            }}
                                        >
                                            <AutoResizeTextarea
                                                value={getCurrentValue(
                                                    field.key,
                                                    selectedLanguage
                                                )}
                                                onChange={(e) =>
                                                    onTextChange(
                                                        field.key,
                                                        e.target.value,
                                                        selectedLanguage
                                                    )
                                                }
                                            />

                                            <div
                                                className="visibility-toggle"
                                                style={{
                                                    pointerEvents: "none",
                                                    opacity: 0.5,
                                                }}
                                            >
                                                <EyeIcon
                                                    height={25}
                                                    width={20}
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}{" "}
                        </tbody>
                    </Table>
                </AvForm>
            </div>
        </div>
    );
});

export default LanguageTable;
